import React from 'react';
import { motion } from 'framer-motion';
import { FiClock, FiCheck, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import useActiveAttendanceSessions from './useActiveAttendanceSessions';
import styles from './ActiveAttendanceSessions.module.css';

// Placeholder mark for the Join Teams Meeting button - swap for the official Teams
// SVG (18x18) whenever it's available; it drops into the same spot.
function TeamsIcon() {
  return <span className={styles.teamsIcon} aria-hidden="true">T</span>;
}

function ActiveAttendanceSessions() {
  const {
    sessions, loading, error, success, checkingIn, updateAvailable, getVisibleSessions,
    handleCheckIn, handleJoinVirtualSession, getSessionStatus, extractWeekNumber,
    getVirtualTimeRangeLabel, getPrimaryStatus, getCheckinDisplay
  } = useActiveAttendanceSessions();

  if (loading && sessions.length === 0) {
    return (
      <div className={styles.attendanceCard}>
        <div className={styles.attendanceHeader}>
          <h2 className={styles.attendanceTitle}>
            <FiClock size={20} />
            Active Attendance Sessions
          </h2>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <span>Loading active sessions...</span>
        </div>
      </div>
    );
  }

  const visibleSessions = getVisibleSessions();

  // Don't show the component at all if there are no sessions or no visible sessions
  if (sessions.length === 0 || visibleSessions.length === 0) {
    return null;
  }

  return (
    <div className={styles.attendanceCard}>
      <div className={styles.attendanceHeader}>
        <h2 className={styles.attendanceTitle}>
          <FiClock size={20} />
          My Active Sessions
          <span className={styles.attendanceTitleCount}>
            {visibleSessions.length} today
          </span>
        </h2>
      </div>

      <div className={styles.attendanceList}>
        {/* Update available banner */}
        {updateAvailable && (
          <motion.div
            className={styles.updateBanner}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className={styles.updateBannerContent}>
              <FiRefreshCw size={16} />
              <span>A new version is available. Please refresh to continue.</span>
            </div>
            <button
              className={styles.updateRefreshButton}
              onClick={() => window.location.reload(true)}
            >
              Refresh Now
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            className={styles.successMessage}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FiCheck size={16} />
            {success}
          </motion.div>
        )}

        {error && !updateAvailable && (
          <motion.div
            className={styles.errorMessage}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FiAlertCircle size={16} />
            {error}
          </motion.div>
        )}

        {visibleSessions.map((session) => {
          const sessionStatus = getSessionStatus(session);
          const isCheckedIn = sessionStatus === 'checked-in';
          const isEnded = sessionStatus === 'ended';
          const weekNumber = extractWeekNumber(session.title);
          const primaryStatus = getPrimaryStatus(session);
          const checkin = getCheckinDisplay(session, isCheckedIn, isEnded);
          const courseCode = session.course_code || session.elective_code;
          const courseName = session.course_name || session.elective_name;
          const instructorName = session.entity_type === 'elective' && session.instructors
            ? session.instructors
            : `${session.instructor_first_name} ${session.instructor_last_name}`;

          return (
            <motion.div
              key={session.session_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0 }}
            >
              <div className={styles.sessionCard}>
                {(primaryStatus || weekNumber) && (
                  <div className={styles.topRow}>
                    {primaryStatus && (
                      <div className={styles.statusGroup}>
                        <span className={`${styles.statusBadge} ${styles[`statusBadge__${primaryStatus.tone}`]}`}>
                          {primaryStatus.tone === 'live' && <span className={styles.statusDot} />}
                          {primaryStatus.label}
                        </span>
                        <span className={`${styles.statusDescription} ${styles.statusDescriptionFull}`}>
                          {primaryStatus.description}
                        </span>
                        <span className={`${styles.statusDescription} ${styles.statusDescriptionShort}`}>
                          {primaryStatus.shortDescription}
                        </span>
                      </div>
                    )}
                    {weekNumber && (
                      <span className={styles.weekPill}>WEEK {weekNumber}</span>
                    )}
                  </div>
                )}

                <div className={styles.courseTitle}>
                  {courseCode} · {courseName}
                </div>
                <div className={styles.instructorLine}>
                  {instructorName} · {getVirtualTimeRangeLabel(session)}
                </div>

                <hr className={styles.divider} />

                <div className={styles.checkinRow}>
                  <div className={styles.checkinInfo}>
                    <div className={styles.checkinTextGroup}>
                      <div className={styles.checkinPrimary}>{checkin.primaryText}</div>
                      <div className={`${styles.checkinSecondary} ${styles.checkinSecondaryFull}`}>
                        {checkin.secondaryText}
                      </div>
                      <div className={`${styles.checkinSecondary} ${styles.checkinSecondaryShort}`}>
                        {checkin.secondaryTextMobile}
                      </div>
                    </div>
                    {checkin.remainingText && (
                      <div className={styles.checkinRemaining}>{checkin.remainingText}</div>
                    )}
                  </div>

                  <div className={styles.actionsGroup}>
                    {(() => {
                      if (isCheckedIn) {
                        // The checkinPrimary/checkinSecondary text above already says
                        // "Checked in at ..." - a disabled button repeating that is redundant.
                        return null;
                      }
                      if (isEnded) {
                        return (
                          <button
                            className={`${styles.actionButton} ${styles.actionButton__closed}`}
                            disabled={true}
                          >
                            Closed
                          </button>
                        );
                      }
                      return (
                        <button
                          className={`${styles.actionButton} ${styles.actionButton__checkin}`}
                          onClick={() => handleCheckIn(session.session_id)}
                          disabled={checkingIn.has(session.session_id)}
                        >
                          {checkingIn.has(session.session_id) ? (
                            <div className={styles.loadingSpinner} />
                          ) : (
                            'Complete Check-In'
                          )}
                        </button>
                      );
                    })()}

                    {session.virtual_link && (
                      <a
                        className={`${styles.actionButton} ${styles.actionButton__join}`}
                        href={session.virtual_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleJoinVirtualSession(session, e)}
                      >
                        <TeamsIcon />
                        Join Teams Meeting
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default ActiveAttendanceSessions;
